
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { logInfo, logError } from "@/utils/logger";

interface AuditLog {
  event_id: string;
  user_id: string;
  timestamp: string;
  action: string;
  details: string;
}

interface AuditLogCardProps {
  logItem?: AuditLog;
  title?: string;
  description?: string;
}

const AuditLogCard: React.FC<AuditLogCardProps> = ({ 
  logItem, 
  title = "Audit Event",
  description = "System activity details"
}) => {
  logInfo("Rendering AuditLogCard", "AuditLogCard", {
    hasLogItem: !!logItem,
    logItemId: logItem?.event_id,
    action: logItem?.action
  });
  
  // If no log item is provided, return a placeholder
  if (!logItem) {
    logInfo("No audit log data available", "AuditLogCard", { 
      title, 
      description 
    });
    
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground py-4">
            No audit logs available
          </p>
        </CardContent>
      </Card>
    );
  }
  
  const { event_id, user_id, timestamp, action, details } = logItem;
  
  const formattedTime = (() => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) {
      logError("Failed to format timestamp", "AuditLogCard", {
        timestamp,
        error: e instanceof Error ? e.message : String(e)
      });
      return timestamp;
    }
  })();
  
  const getActionColor = (action: string) => {
    logInfo("Determining action color", "AuditLogCard", { action });
    if (action.includes('login')) return 'bg-blue-100 text-blue-800';
    if (action.includes('create')) return 'bg-green-100 text-green-800';
    if (action.includes('export')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex flex-col space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-gray-500">{event_id}</span>
            <span className="text-xs text-gray-500">{formattedTime}</span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">User: {user_id}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(action)}`}>
              {action}
            </span>
          </div>
          
          <div className="text-sm mt-2 text-gray-600">
            {details}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuditLogCard;
